import { useEffect, useRef, useState, type RefObject } from "react";
import { AppState, Platform } from "react-native";
import type WebView from "react-native-webview";

const PAUSE_MEDIA_JS = `
  (function(){
    try {
      document.querySelectorAll('video,audio').forEach(function(el){
        el.pause();
      });
    } catch (e) {}
    true;
  })();
`;

/**
 * Guards WebView native calls across unmount / background.
 *
 * Android Chromium + GPU drivers (especially MediaTek) SIGSEGV when a
 * hardware surface is drawn or messaged after the view is detached.
 */
export const useWebViewNativeLifecycle = (
  webviewRef: RefObject<WebView | null>
) => {
  const isMountedRef = useRef(true);
  const isNativeAliveRef = useRef(true);
  const [isAppActive, setIsAppActive] = useState(
    () => AppState.currentState === "active"
  );

  useEffect(() => {
    isMountedRef.current = true;
    isNativeAliveRef.current = true;

    const pauseNative = () => {
      if (!isNativeAliveRef.current) return;
      try {
        webviewRef.current?.injectJavaScript?.(PAUSE_MEDIA_JS);
      } catch {
        // Native view may already be torn down.
      }
    };

    const sub = AppState.addEventListener("change", (state) => {
      const active = state === "active";
      setIsAppActive(active);
      if (!active) {
        pauseNative();
      }
    });

    return () => {
      isNativeAliveRef.current = false;
      isMountedRef.current = false;
      sub.remove();
    };
  }, [webviewRef]);

  const keepNativeSurface = Platform.OS !== "android" || isAppActive;

  return { isMountedRef, keepNativeSurface };
};
